import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import axios from "axios";

// normalizador de trámite
const normTramite = (t) => ({
    id: t.id,
    nombre: t.nombre,
    descripcion: t.descripcion,
    numArchivos: Number(t.numArchivos ?? t.num_archivos ?? 0),
});

// clave única para deduplicar archivos
const fileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

const TramitesPublicos = () => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

    const [tramites, setTramites] = useState([]);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [tramiteSel, setTramiteSel] = useState(null);
    const [archivos, setArchivos] = useState([]);

    const [colegiadoId, setColegiadoId] = useState(null);
    const [estadoPorTramite, setEstadoPorTramite] = useState({}); // { [tramiteId]: PENDIENTE|FINALIZADA|RECHAZADA }

    // 1) Cargar catálogo de trámites
    useEffect(() => {
        axios
            .get(`${API}/api/tramites`)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data.map(normTramite) : [];
                setTramites(list);
            })
            .catch((err) => console.error("Error al cargar trámites", err));
    }, [API]);

    // 2) Cargar perfil para saber mi código
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;
        axios
            .get(`${API}/colegiado/perfil`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setColegiadoId(res?.data?.codigo))
            .catch((err) => console.error("Error obteniendo perfil", err));
    }, [API]);

    // 3) Cargar mis estados de trámites (cuando ya tengo mi código)
    useEffect(() => {
        if (!colegiadoId) return;
        const token = localStorage.getItem("token");
        axios
            .get(`${API}/api/colegiados/${colegiadoId}/tramites/page`, {
                params: { page: 0, size: 1000 },
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const content = Array.isArray(res?.data?.content) ? res.data.content : [];
                const mapa = {};
                for (const it of content) {
                    const tId = it.tramiteId ?? it.tramite_id ?? it?.id?.tramiteId ?? it?.id?.tramite_id;
                    const est = it.estado;
                    if (tId) mapa[tId] = est;
                }
                setEstadoPorTramite(mapa);
            })
            .catch((err) => console.error("Error cargando mis trámites", err));
    }, [colegiadoId, API]);

    // abrir / cerrar modal
    const abrirModal = (t) => {
        if (!colegiadoId) {
            alert("Debes iniciar sesión.");
            return;
        }
        setTramiteSel(t);
        setArchivos([]);
        setModalAbierto(true);
    };
    const cerrarModal = () => {
        setModalAbierto(false);
        setTramiteSel(null);
        setArchivos([]);
    };

    const quitarArchivo = (idx) => {
        setArchivos((prev) => prev.filter((_, i) => i !== idx));
    };

    // subir docs usuario
    const subirArchivosUsuario = async (nombreParaPrefijo) => {
        const token = localStorage.getItem("token");
        for (let i = 0; i < archivos.length; i++) {
            const archivo = archivos[i];
            const ext = archivo.name.includes(".") ? archivo.name.split(".").pop() : "";
            const base = (nombreParaPrefijo || "tramite").trim().replace(/\s+/g, "_");
            const nuevoNombre = ext ? `${base}_${i + 1}.${ext}` : `${base}_${i + 1}`;

            let payloadFile = archivo;
            try {
                payloadFile = new File([archivo], nuevoNombre, { type: archivo.type });
            } catch (_) {
                // Safari/iOS fallback
            }

            const fd = new FormData();
            fd.append("file", payloadFile);
            fd.append("colegiadoId", String(colegiadoId));

            await axios.post(`${API}/api/documentos/subir`, fd, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });
        }
    };

    // solicitar trámite (crea/actualiza relación a PENDIENTE)
    const solicitarTramite = async (tramiteId) => {
        const token = localStorage.getItem("token");
        // preferimos /solicitar; si no existe, fallback a POST simple
        try {
            await axios.post(
                `${API}/api/colegiados/${colegiadoId}/tramites/self/${tramiteId}`,
                null,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return;
        } catch (err) {
            if (err?.response?.status !== 404) throw err; // si no es 404, propaga
        }
        // fallback
        await axios.post(
            `${API}/api/colegiados/${colegiadoId}/tramites/${tramiteId}`,
            null,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    };

    const handleEnviarSolicitud = async () => {
        if (!tramiteSel || !colegiadoId) return;

        const requeridos = Number(tramiteSel?.numArchivos ?? 0);
        // si hay requeridos > 0, exige al menos esa cantidad
        if (requeridos > 0 && archivos.length < requeridos) {
            alert(`Debes adjuntar al menos ${requeridos} archivo(s).`);
            return;
        }

        try {
            // 1) subir documentos del colegiado
            if (archivos.length > 0) {
                await subirArchivosUsuario(tramiteSel.nombre || "Tramite");
            }

            // 2) crear/actualizar relación a PENDIENTE
            await solicitarTramite(tramiteSel.id);

            // 3) refrescar estado local
            setEstadoPorTramite((prev) => ({ ...prev, [tramiteSel.id]: "PENDIENTE" }));
            alert("Trámite solicitado correctamente.");
            cerrarModal();
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            if (status === 409) {
                alert("Ya existe una solicitud para este trámite.");
            } else {
                const msg = err?.response?.data || "Error al solicitar el trámite.";
                alert(Array.isArray(msg) ? msg.join("\n") : String(msg));
            }
        }
    };

    // Habilitar botón Enviar
    const requeridos = Number(tramiteSel?.numArchivos ?? 0);
    const puedeEnviar = requeridos === 0 ? archivos.length >= 0 : archivos.length >= requeridos;

    return (
        <Paper elevation={3} sx={{ padding: 2, margin: 2 }}>
            <Box p={3}>
                <Typography variant="h4" gutterBottom>
                    Trámites disponibles
                </Typography>

                {tramites.length === 0 ? (
                    <Typography>No hay trámites publicados.</Typography>
                ) : (
                    tramites.map((t0) => {
                        const t = normTramite(t0);
                        const estado = estadoPorTramite[t.id]; // PENDIENTE | FINALIZADA | RECHAZADA | undefined
                        const bloqueado = estado === "PENDIENTE" || estado === "FINALIZADA";

                        return (
                            <Paper key={t.id} elevation={2} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="h6">
                                    {t.nombre}
                                    <span className="ml-2 text-sm text-gray-500 font-normal">
                                        {" "}
                                        · Requiere {t.numArchivos} archivo(s)
                                    </span>
                                </Typography>

                                <div
                                    className="tiptap-rendered"
                                    dangerouslySetInnerHTML={{ __html: t.descripcion }}
                                />

                                {estado ? (
                                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <span
                                            className={`px-2 py-0.5 rounded text-xs ${estado === "FINALIZADA"
                                                    ? "bg-green-100 text-green-800"
                                                    : estado === "RECHAZADA"
                                                        ? "bg-red-100 text-red-800"
                                                        : "bg-yellow-100 text-yellow-800"
                                                }`}
                                        >
                                            Estado: {estado}
                                        </span>

                                        {estado === "RECHAZADA" ? (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{ mt: 1 }}
                                                onClick={() => abrirModal(t)}
                                            >
                                                Volver a solicitar
                                            </Button>
                                        ) : (
                                            <Button variant="outlined" size="small" sx={{ mt: 1 }} disabled>
                                                {estado === "FINALIZADA" ? "Finalizada" : "Pendiente"}
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ mt: 1 }}
                                        onClick={() => abrirModal(t)}
                                        disabled={bloqueado}
                                    >
                                        Solicitar
                                    </Button>
                                )}
                            </Paper>
                        );
                    })
                )}
            </Box>

            {/* MODAL */}
            <Dialog open={modalAbierto} onClose={cerrarModal} fullWidth>
                <DialogTitle>Solicitar: {tramiteSel?.nombre}</DialogTitle>
                <DialogContent dividers>
                    <Typography mb={1}>
                        {requeridos > 0 ? (
                            <>
                                Debes subir <strong>{requeridos}</strong> archivo(s) obligatorios.
                            </>
                        ) : (
                            "Puedes adjuntar archivos si lo necesitas."
                        )}
                    </Typography>

                    <input
                        key={modalAbierto ? "open" : "closed"}
                        type="file"
                        multiple
                        onChange={(e) => {
                            const nuevos = Array.from(e.target.files || []);
                            setArchivos((prev) => {
                                const mapa = new Map(prev.map((f) => [fileKey(f), f]));
                                for (const f of nuevos) mapa.set(fileKey(f), f);
                                return Array.from(mapa.values());
                            });
                            e.target.value = "";
                        }}
                    />

                    {/* listado simple */}
                    <Box mt={1}>
                        {archivos.map((f, idx) => (
                            <Box
                                key={fileKey(f)}
                                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                            >
                                <Typography variant="body2" sx={{ flex: 1 }} title={f.name}>
                                    {f.name} ({Math.round(f.size / 1024)} KB)
                                </Typography>
                                <Button size="small" onClick={() => quitarArchivo(idx)}>
                                    Quitar
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarModal}>Cancelar</Button>
                    <Button variant="contained" disabled={!puedeEnviar} onClick={handleEnviarSolicitud}>
                        Enviar solicitud
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default TramitesPublicos;
